import { Fragment, useEffect, useState } from 'react';
import { useModal } from '@belong/common';
import { Toast, Text, DashedLine } from '@belong/ui';
import { CollapseFlowSection } from 'accounts/components/collapse-flow-section/collapse-flow-section';
import { HomeAccessInstructionsSection } from 'accounts/components/home-access-instructions-section/home-access-instructions-section';
import { UpcomingAppointmentsSection } from 'accounts/components/upcoming-appointments-section/upcoming-appointments-section';
import { useFeatureFlag } from 'hooks/use-account-redesign';
import Address from 'models/common/Address';
import UnitLease from 'models/common/UnitLeaseDetail';
import HomeownerAccountProperty from 'models/homeownerAccounts/HomeownerAccountProperty';
import { HomeImprovementsTracking } from './home-improvements-tracking/home-improvements-tracking';

type SetupFlowSectionsProps = {
  // An array, not a one-element tuple, and nullable while the flows load —
  // which is why it is read as `properties?.find(...)` below.
  properties: HomeownerAccountProperty[] | null;
  propertySection: {
    sectionTitle: string;
    unitId: string;
    timeZone: string;
    subSections: {
      name: string;
      isCompleted: boolean;
    }[];
  }[];
  accountSection: {
    sectionTitle: string;
    unitId: string;
    timeZone: string;
    subSections: {
      name: string;
      isCompleted: boolean;
    }[];
  };
  shouldShowCommonSteps: boolean;
  openInitialModal: (unit: UnitLease, address: Address) => void;
};

export function SetupFlowSections({
  properties,
  propertySection,
  accountSection,
  shouldShowCommonSteps,
  openInitialModal,
}: SetupFlowSectionsProps) {
  const { isEnabled: isAccountRedesignEnabled } =
    useFeatureFlag('account-redesign');
  const [isToastOpen, openToast, closeToast] = useModal();
  const urlParams = new URLSearchParams(window.location.search);
  const hoFixingDate = urlParams.get('hoFixingDate');
  const [memoizedFixingDate] = useState<string | null>(hoFixingDate);
  const [
    propertySectionTitleVisibilityByProperty,
    setPropertySectionTitleVisibilityByProperty,
  ] = useState<
    Record<
      string,
      Partial<
        Record<
          | 'upcoming-appointments'
          | 'home-access-instructions'
          | 'home-improvements-tracking',
          boolean
        >
      >
    >
  >({});

  useEffect(() => {
    if (hoFixingDate) {
      openToast();
    }
  }, [openToast, hoFixingDate]);

  const handleToastClose = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('hoFixingDate');
    window.history.replaceState({}, '', url.toString());
    closeToast();
  };

  return (
    <>
      {propertySection.map((section, index) => {
        const unitProperty = properties?.find((property) =>
          property.units.some(
            (unit: any) => unit?.basicInfo?.unitId === section?.unitId,
          ),
        );

        const selectedUnit = unitProperty?.units?.find(
          (unit: any) => unit?.basicInfo?.unitId === section?.unitId,
        );

        const inspectionScheduleSubSection = section.subSections.find(
          (subSection) => subSection.name === 'InspectionSchedule',
        );

        const isTitleVisibleForCurrentUnit = Object.values(
          propertySectionTitleVisibilityByProperty[section.unitId] ?? {},
        ).some(Boolean);

        return (
          <Fragment key={section.unitId}>
            {isAccountRedesignEnabled &&
              isTitleVisibleForCurrentUnit &&
              index !== 0 && (
                <div
                  className="mb-xl flex w-full items-center justify-center"
                  style={{ height: 2 }}
                >
                  <DashedLine />
                </div>
              )}
            {isAccountRedesignEnabled && isTitleVisibleForCurrentUnit && (
              <div className="mb-xl">
                <Text fontWeight="semibold" variant="h3">
                  {section.sectionTitle}
                </Text>
              </div>
            )}
            <UpcomingAppointmentsSection
              // The sections being mapped are built from these same properties,
              // so the one matching this section's unit is always found.
              coverageRegionId={unitProperty!.basicInfo.coverageRegionId}
              timeZone={section.timeZone}
              unit={selectedUnit}
              setPropertySectionTitleVisibilityByProperty={
                setPropertySectionTitleVisibilityByProperty
              }
            />
            {inspectionScheduleSubSection?.isCompleted && (
              <HomeAccessInstructionsSection
                unit={selectedUnit}
                setPropertySectionTitleVisibilityByProperty={
                  setPropertySectionTitleVisibilityByProperty
                }
              />
            )}
            <HomeImprovementsTracking
              section={section}
              setPropertySectionTitleVisibilityByProperty={
                setPropertySectionTitleVisibilityByProperty
              }
            />
            {!isAccountRedesignEnabled && (
              <CollapseFlowSection
                properties={properties}
                key={section.sectionTitle}
                section={section}
                shouldShowCommonSteps={shouldShowCommonSteps}
                openInitialModal={openInitialModal}
              />
            )}
          </Fragment>
        );
      })}
      {!isAccountRedesignEnabled && shouldShowCommonSteps && accountSection && (
        <div className="mb-xl flex flex-col">
          <CollapseFlowSection
            properties={properties}
            section={accountSection}
            shouldShowCommonSteps={shouldShowCommonSteps}
            openInitialModal={openInitialModal}
          />
        </div>
      )}
      <Toast
        isVisible={isToastOpen}
        onClose={handleToastClose}
        variant="default"
      >
        {memoizedFixingDate === 'completed'
          ? `Great, We'll confirm the work so we can prepare your home to be\u00a0loved!`
          : `Success! You confirmed that you are on track to finish ${memoizedFixingDate}`}
      </Toast>
    </>
  );
}
